import {Controller} from "@hotwired/stimulus"

// The story template width is the shared default. A drag here is remembered
// only in this browser, so another person still gets the template width.
export default class extends Controller {
  static targets = ["handle"]
  static values = {
    key: String,
    edge: {type: String, default: "left"},
    min: {type: Number, default: 160},
    max: {type: Number, default: 640}
  }

  connect() {
    this.defaultWidth = this.element.style.width
    const saved = this.readWidth()
    if (saved) this.element.style.width = `${saved}px`
    this.syncSlider()
  }

  start(event) {
    if (event.button != null && event.button !== 0) return
    event.preventDefault()
    this.dragging = true
    this.startX = event.clientX
    this.startWidth = this.element.getBoundingClientRect().width
    event.currentTarget.setPointerCapture?.(event.pointerId)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  drag(event) {
    if (!this.dragging) return
    this.applyWidth(this.startWidth + this.delta(event.clientX))
  }

  end() {
    if (!this.dragging) return
    this.dragging = false
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    this.persist()
  }

  nudge(event) {
    if (event.key === "Home") {
      event.preventDefault()
      this.reset()
      return
    }
    const wider = this.edgeValue === "right" ? "ArrowLeft" : "ArrowRight"
    const narrower = this.edgeValue === "right" ? "ArrowRight" : "ArrowLeft"
    let step = 0
    if (event.key === wider) step = 16
    else if (event.key === narrower) step = -16
    else return
    event.preventDefault()
    this.applyWidth(this.element.getBoundingClientRect().width + step)
    this.persist()
  }

  reset(event) {
    event?.preventDefault()
    this.dragging = false
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    try {
      localStorage.removeItem(this.storageKey())
    } catch {
      // Private browsing can reject storage. The width still resets for this visit.
    }
    this.element.style.width = this.defaultWidth || ""
    this.syncSlider()
  }

  applyWidth(px) {
    this.element.style.width = `${this.clamp(px)}px`
    this.syncSlider()
  }

  delta(clientX) {
    const dx = clientX - this.startX
    return this.edgeValue === "right" ? -dx : dx
  }

  clamp(px) {
    return Math.round(Math.min(this.maxValue, Math.max(this.minValue, px)))
  }

  readWidth() {
    try {
      const raw = localStorage.getItem(this.storageKey())
      if (raw == null || raw === "") return null
      const width = Number(raw)
      if (!Number.isFinite(width)) return null
      return this.clamp(width)
    } catch {
      return null
    }
  }

  persist() {
    const width = Math.round(this.element.getBoundingClientRect().width)
    try {
      localStorage.setItem(this.storageKey(), String(width))
    } catch {
      // Same private-browsing case as reset: the drag still applies for this visit.
    }
  }

  syncSlider() {
    if (!this.hasHandleTarget) return
    const width = Math.round(this.element.getBoundingClientRect().width)
    this.handleTarget.setAttribute("aria-valuemin", String(this.minValue))
    this.handleTarget.setAttribute("aria-valuemax", String(this.maxValue))
    if (width <= 0) return
    this.handleTarget.setAttribute("aria-valuenow", String(width))
    this.handleTarget.setAttribute("aria-valuetext", `${width} pixels`)
  }

  storageKey() {
    return this.keyValue || "story-sidebar-width"
  }
}
